import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-[#050811]/75 backdrop-blur-xl border-cyan-400/15" : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <button onClick={() => go("top")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src="/assets/logo-mark.png" alt="UNDERCLEAN" className="h-10 w-10 sm:h-11 sm:w-11 rounded-full transition-transform duration-500 group-hover:rotate-[20deg]" />
          <span className="font-display font-semibold tracking-[0.18em] text-sm sm:text-base text-brand">UNDERCLEAN</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              className="relative text-sm text-slate-300 hover:text-white transition-colors duration-300 group"
              data-testid={`nav-link-${n.id}`}
            >
              {n.label}
              <span className="absolute left-0 -bottom-1 h-px w-full bg-cyan-300 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("devis")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand text-[#050811] font-semibold text-sm px-5 py-2.5 hover:shadow-[0_0_32px_rgba(10,245,240,0.45)] transition-shadow duration-500"
            data-testid="nav-btn-quote"
          >
            Demander un devis <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="md:hidden h-11 w-11 rounded-full border border-cyan-400/30 flex items-center justify-center text-white"
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="md:hidden bg-[#050811]/95 backdrop-blur-xl border-t border-cyan-400/15 px-5 pb-8 pt-4"
            data-testid="nav-mobile-menu"
          >
            {NAV.map((n, i) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="flex w-full items-baseline justify-between py-4 border-b border-white/5 font-display text-2xl text-white"
                data-testid={`nav-mobile-link-${n.id}`}
              >
                {n.label}
                <span className="font-mono-uc text-xs text-cyan-300">0{i + 1}</span>
              </button>
            ))}
            <button
              onClick={() => go("devis")}
              className="mt-6 w-full rounded-full bg-brand text-[#050811] font-semibold py-3.5"
              data-testid="nav-mobile-quote"
            >
              Demander un devis
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
